import {
  BuildingOfficeIcon,
  GlobeAsiaAustraliaIcon,
  BuildingStorefrontIcon,
  StarIcon,
  UserGroupIcon,
  UserIcon
} from '@heroicons/react/24/outline';

// User type constants
export const USER_TYPES = {
  ADMIN: 'admin',
  COUNTRY_ADMIN: 'country',
  CITY_ADMIN: 'city',
  SUPER_AGENT: 'super-agent',
  AGENT: 'agent',
  PLAYER: 'player'
};

// Root first, player last. Used to order the hierarchy ladder when a limit can
// be attached anywhere between the platform and a single player.
export const HIERARCHY_ORDER = [
  USER_TYPES.ADMIN,
  USER_TYPES.COUNTRY_ADMIN,
  USER_TYPES.CITY_ADMIN,
  USER_TYPES.SUPER_AGENT,
  USER_TYPES.AGENT,
  USER_TYPES.PLAYER
];

// Mirrors the gate TopNav uses to show the Limits entry. Anything that offers a
// shortcut into the limits flow has to agree with the nav, or users get a button
// that leads to a page they cannot reach.
export const LIMITS_MANAGER_TYPES = [
  USER_TYPES.ADMIN,
  USER_TYPES.COUNTRY_ADMIN,
  USER_TYPES.CITY_ADMIN
];

export const canManageLimits = (userType) =>
  LIMITS_MANAGER_TYPES.includes(String(userType || '').toLowerCase());

// Game providers list
export const GAME_PROVIDERS = [
  { id: 'all', name: 'All Providers' },
  { id: 'pragmatic', name: 'Pragmatic Play' },
  { id: 'worldmatch', name: 'WorldMatch' },
  { id: 'playson', name: 'Playson' },
  { id: 'booming', name: 'Booming Games' },
  { id: 'mascot', name: 'Mascot Gaming' },
  { id: 'digitain-galaxsys', name: 'Digitain/Galaxsys' },
  { id: 'pateplay', name: 'PatePlay' },
  { id: 'fazi', name: 'Fazi' },
  { id: 'evo-redtiger', name: 'Evolution - Red Tiger' },
  { id: 'evo-evolution', name: 'Evolution Gaming' },
  { id: 'evo-netent', name: 'Evolution - NetEnt' },
  { id: 'pgsoft', name: 'PG Soft' },
  { id: 'popok', name: 'PopOK Gaming' },
  { id: 'creedroomz', name: 'CreedRoomz' },
  { id: 'pascal-gaming', name: 'Pascal Gaming' },
  { id: 'hacksaw', name: 'Hacksaw Gaming' },
  { id: 'amusnet', name: 'Amusnet' },
  { id: '3oaks', name: '3 Oaks Gaming' }
];

// Icon options for sports events
export const SPORTS_ICONS = [
  // Trophies & Medals
  { value: '🏆', label: 'Trophy', category: 'general' },
  { value: '🥇', label: 'Gold Medal', category: 'general' },
  { value: '🥈', label: 'Silver Medal', category: 'general' },
  { value: '🥉', label: 'Bronze Medal', category: 'general' },
  { value: '🏅', label: 'Medal', category: 'general' },
  // Sports
  { value: '⚽', label: 'Soccer', category: 'sports' },
  { value: '🏀', label: 'Basketball', category: 'sports' },
  { value: '🏈', label: 'American Football', category: 'sports' },
  { value: '⚾', label: 'Baseball', category: 'sports' },
  { value: '🎾', label: 'Tennis', category: 'sports' },
  { value: '🏐', label: 'Volleyball', category: 'sports' },
  { value: '🏒', label: 'Hockey', category: 'sports' },
  { value: '🏓', label: 'Table Tennis', category: 'sports' },
  { value: '🏸', label: 'Badminton', category: 'sports' },
  { value: '🥊', label: 'Boxing', category: 'sports' },
  { value: '🎱', label: 'Billiards', category: 'sports' },
  { value: '🏁', label: 'Racing', category: 'sports' },
  // Regions
  { value: '🌍', label: 'World/Global', category: 'region' },
  { value: '🌎', label: 'Americas', category: 'region' },
  { value: '🌏', label: 'Asia', category: 'region' },
  { value: '🇪🇺', label: 'Europe', category: 'region' },
  // Country Flags
  { value: '🇱🇧', label: 'Lebanon', category: 'country' },
  { value: '🇪🇬', label: 'Egypt', category: 'country' },
  { value: '🇸🇦', label: 'Saudi Arabia', category: 'country' },
  { value: '🇦🇪', label: 'UAE', category: 'country' },
  { value: '🇶🇦', label: 'Qatar', category: 'country' },
  { value: '🇲🇦', label: 'Morocco', category: 'country' },
  { value: '🇹🇳', label: 'Tunisia', category: 'country' },
  { value: '🇯🇴', label: 'Jordan', category: 'country' },
  { value: '🇰🇼', label: 'Kuwait', category: 'country' },
  { value: '🇧🇭', label: 'Bahrain', category: 'country' },
  { value: '🇺🇸', label: 'USA', category: 'country' },
  { value: '🇬🇧', label: 'UK', category: 'country' },
  { value: '🇪🇸', label: 'Spain', category: 'country' },
  { value: '🇩🇪', label: 'Germany', category: 'country' },
  { value: '🇮🇹', label: 'Italy', category: 'country' },
  { value: '🇫🇷', label: 'France', category: 'country' },
  { value: '🇧🇷', label: 'Brazil', category: 'country' },
  { value: '🇦🇷', label: 'Argentina', category: 'country' },
  // Special
  { value: '🎮', label: 'E-Sports', category: 'special' },
  { value: '👶', label: 'Youth', category: 'special' },
  { value: '👩', label: 'Women', category: 'special' },
  { value: '📊', label: 'Statistics', category: 'special' },
  { value: '🎯', label: 'Target/Goal', category: 'special' },
  { value: '📐', label: 'Corners', category: 'special' },
  { value: '🟨', label: 'Yellow Card', category: 'special' },
  { value: '🟥', label: 'Red Card', category: 'special' },
  { value: '🥅', label: 'Goal/Net', category: 'special' }
];

// Sports events/categories list.
// You can add entries directly in code, OR use the "+ Add Custom Event" button in the UI.
// id, name and category must match the coreadmin BUILT_IN catalog entry for the same id,
// and the aliases are what let a raw league name off a bet resolve back to one of these.
export const SPORTS_EVENTS = [
  {
    id: 'all-sports',
    name: 'All Sports',
    category: 'universal',
    aliases: ['all sports', 'sports', 'sportsbook', 'everything sports', 'kel el sports', 'riyada', 'رياضة']
  },
  // Major International Events
  { id: 'worldcup', name: 'FIFA World Cup', category: 'event', icon: '🏆', aliases: ['world cup', 'fifa world cup', 'mondial', 'kas el 3alam', 'kass el alam', 'كاس العالم'] },
  { id: 'olympics', name: 'Olympics', category: 'event', icon: '🥇', aliases: ['olympics', 'olympic games', 'jeux olympiques', 'olombiad', 'اولمبياد'] },
  { id: 'african-cup', name: 'African Cup of Nations', category: 'event', icon: '🌍', aliases: ['african cup', 'afcon', 'can', 'cup of nations', 'kas afrikia', 'كاس افريقيا'] },
  { id: 'arab-cup', name: 'Arab Cup', category: 'event', icon: '🏆', aliases: ['arab cup', 'kas el 3arab', 'كاس العرب'] },
  { id: 'euro-cup', name: 'UEFA Euro Cup', category: 'event', icon: '🇪🇺', aliases: ['euro', 'euro cup', 'uefa euro', 'euros', 'yoro', 'امم اوروبا'] },
  { id: 'copa-america', name: 'Copa America', category: 'event', icon: '🌎', aliases: ['copa america', 'copa', 'kopa amerika'] },
  { id: 'conference-league', name: 'UEFA Conference League', category: 'event', icon: '⚽', aliases: ['conference league', 'uefa conference league', 'uecl'] },
  { id: 'nations-league', name: 'UEFA Nations League', category: 'event', icon: '🇪🇺', aliases: ['nations league', 'uefa nations league'] },
  { id: 'club-world-cup', name: 'FIFA Club World Cup', category: 'event', icon: '🏆', aliases: ['club world cup', 'fifa club world cup'] },
  { id: 'asian-cup', name: 'AFC Asian Cup', category: 'event', icon: '🌏', aliases: ['asian cup', 'afc asian cup', 'kas asia'] },
  { id: 'gold-cup', name: 'CONCACAF Gold Cup', category: 'event', icon: '🌎', aliases: ['gold cup', 'concacaf gold cup'] },
  { id: 'libertadores', name: 'Copa Libertadores', category: 'event', icon: '🏆', aliases: ['copa libertadores', 'libertadores', 'conmebol libertadores'] },
  { id: 'sudamericana', name: 'Copa Sudamericana', category: 'event', icon: '🏆', aliases: ['copa sudamericana', 'sudamericana'] },
  { id: 'fa-cup', name: 'FA Cup', category: 'event', icon: '🇬🇧', aliases: ['fa cup', 'english fa cup', 'england fa cup'] },
  { id: 'efl-cup', name: 'EFL Cup', category: 'event', icon: '🇬🇧', aliases: ['efl cup', 'carabao cup', 'england league cup'] },
  { id: 'copa-del-rey', name: 'Copa del Rey', category: 'event', icon: '🇪🇸', aliases: ['copa del rey', 'spanish cup', 'spain copa del rey'] },
  { id: 'dfb-pokal', name: 'DFB Pokal', category: 'event', icon: '🇩🇪', aliases: ['dfb pokal', 'german cup', 'germany dfb pokal'] },
  { id: 'coppa-italia', name: 'Coppa Italia', category: 'event', icon: '🇮🇹', aliases: ['coppa italia', 'italian cup'] },
  { id: 'coupe-de-france', name: 'Coupe de France', category: 'event', icon: '🇫🇷', aliases: ['coupe de france', 'french cup'] },
  { id: 'afc-champions-league', name: 'AFC Champions League', category: 'event', icon: '🌏', aliases: ['afc champions league', 'asian champions league'] },
  { id: 'caf-champions-league', name: 'CAF Champions League', category: 'event', icon: '🌍', aliases: ['caf champions league', 'african champions league'] },
  { id: 'wimbledon', name: 'Wimbledon', category: 'event', icon: '🎾', aliases: ['wimbledon', 'the championships'] },
  { id: 'us-open-tennis', name: 'US Open', category: 'event', icon: '🎾', aliases: ['us open', 'us open tennis'] },
  { id: 'french-open', name: 'French Open', category: 'event', icon: '🎾', aliases: ['french open', 'roland garros'] },
  { id: 'australian-open', name: 'Australian Open', category: 'event', icon: '🎾', aliases: ['australian open'] },
  // Major Leagues
  { id: 'nba', name: 'NBA (Basketball)', category: 'league', icon: '🏀', aliases: ['nba', 'basketball', 'basket', 'american basketball', 'كرة السلة'] },
  { id: 'premier-league', name: 'Premier League', category: 'league', icon: '🇬🇧', aliases: ['premier league', 'premier', 'epl', 'english league', 'england', 'el dawre el inglizi', 'dawri inglizi', 'الدوري الانجليزي'] },
  { id: 'la-liga', name: 'La Liga', category: 'league', icon: '🇪🇸', aliases: ['la liga', 'laliga', 'spanish league', 'spain', 'el dawre el spanyole', 'الدوري الاسباني'] },
  { id: 'bundesliga', name: 'Bundesliga', category: 'league', icon: '🇩🇪', aliases: ['bundesliga', 'german league', 'germany', 'el dawre el almane', 'الدوري الالماني'] },
  { id: 'serie-a', name: 'Serie A', category: 'league', icon: '🇮🇹', aliases: ['serie a', 'seria', 'italian league', 'italy', 'el dawre el itale', 'الدوري الايطالي'] },
  { id: 'ligue-1', name: 'Ligue 1', category: 'league', icon: '🇫🇷', aliases: ['ligue 1', 'ligue un', 'french league', 'france', 'el dawre el fransawe', 'الدوري الفرنسي'] },
  { id: 'champions-league', name: 'Champions League', category: 'league', icon: '🏆', aliases: ['champions league', 'champions', 'ucl', 'abtal oroba', 'dawre el abtal', 'دوري ابطال اوروبا'] },
  { id: 'europa-league', name: 'Europa League', category: 'league', icon: '⚽', aliases: ['europa league', 'europa', 'uel'] },
  { id: 'nfl', name: 'NFL (American Football)', category: 'league', icon: '🏈', aliases: ['nfl', 'american football', 'football americain'] },
  { id: 'nhl', name: 'NHL (Hockey)', category: 'league', icon: '🏒', aliases: ['nhl', 'hockey', 'ice hockey', 'هوكي'] },
  { id: 'mlb', name: 'MLB (Baseball)', category: 'league', icon: '⚾', aliases: ['mlb', 'baseball', 'بيسبول'] },
  { id: 'championship', name: 'EFL Championship', category: 'league', icon: '🇬🇧', aliases: ['efl championship', 'english championship', 'championship'] },
  { id: 'league-one', name: 'EFL League One', category: 'league', icon: '🇬🇧', aliases: ['efl league one', 'english league one', 'league one'] },
  { id: 'league-two', name: 'EFL League Two', category: 'league', icon: '🇬🇧', aliases: ['efl league two', 'english league two', 'league two'] },
  { id: 'segunda-division', name: 'Segunda Division', category: 'league', icon: '🇪🇸', aliases: ['segunda division', 'spanish segunda', 'laliga 2', 'la liga 2'] },
  { id: 'bundesliga-2', name: '2. Bundesliga', category: 'league', icon: '🇩🇪', aliases: ['2 bundesliga', 'bundesliga 2', 'second bundesliga'] },
  { id: 'serie-b', name: 'Serie B', category: 'league', icon: '🇮🇹', aliases: ['serie b', 'italian serie b'] },
  { id: 'ligue-2', name: 'Ligue 2', category: 'league', icon: '🇫🇷', aliases: ['ligue 2', 'french ligue 2'] },
  { id: 'eredivisie', name: 'Eredivisie', category: 'league', icon: '🇳🇱', aliases: ['eredivisie', 'dutch eredivisie', 'netherlands eredivisie'] },
  { id: 'primeira-liga', name: 'Primeira Liga', category: 'league', icon: '🇵🇹', aliases: ['primeira liga', 'liga portugal', 'portuguese league'] },
  { id: 'belgian-pro-league', name: 'Belgian Pro League', category: 'league', icon: '🇧🇪', aliases: ['belgian pro league', 'jupiler pro league'] },
  { id: 'scottish-premiership', name: 'Scottish Premiership', category: 'league', icon: '🏴󠁧󠁢󠁳󠁣󠁴󠁿', aliases: ['scottish premiership', 'spfl', 'scottish premier league'] },
  { id: 'super-lig', name: 'Turkish Super Lig', category: 'league', icon: '🇹🇷', aliases: ['super lig', 'turkish super lig', 'süper lig', 'turkey super lig'] },
  { id: 'russian-premier-league', name: 'Russian Premier League', category: 'league', icon: '🇷🇺', aliases: ['russian premier league', 'russia premier league'] },
  { id: 'ukrainian-premier-league', name: 'Ukrainian Premier League', category: 'league', icon: '🇺🇦', aliases: ['ukrainian premier league', 'ukraine premier league'] },
  { id: 'mls', name: 'MLS', category: 'league', icon: '🇺🇸', aliases: ['mls', 'major league soccer'] },
  { id: 'liga-mx', name: 'Liga MX', category: 'league', icon: '🇲🇽', aliases: ['liga mx', 'mexican league', 'liga bbva mx'] },
  { id: 'brasileirao', name: 'Brasileirao', category: 'league', icon: '🇧🇷', aliases: ['brasileirao', 'brasileiro', 'brazilian league'] },
  { id: 'argentina-primera', name: 'Argentine Primera', category: 'league', icon: '🇦🇷', aliases: ['argentine primera', 'liga profesional argentina', 'primera division argentina'] },
  { id: 'colombia-primera', name: 'Colombian Primera', category: 'league', icon: '🇨🇴', aliases: ['colombian primera', 'liga betplay', 'colombia primera'] },
  { id: 'chile-primera', name: 'Chilean Primera', category: 'league', icon: '🇨🇱', aliases: ['chilean primera', 'chile primera', 'campeonato nacional'] },
  { id: 'greek-super-league', name: 'Greek Super League', category: 'league', icon: '🇬🇷', aliases: ['greek super league', 'super league greece'] },
  { id: 'swiss-super-league', name: 'Swiss Super League', category: 'league', icon: '🇨🇭', aliases: ['swiss super league', 'super league switzerland'] },
  { id: 'austrian-bundesliga', name: 'Austrian Bundesliga', category: 'league', icon: '🇦🇹', aliases: ['austrian bundesliga', 'austria bundesliga'] },
  { id: 'danish-superliga', name: 'Danish Superliga', category: 'league', icon: '🇩🇰', aliases: ['danish superliga', 'superliga', 'denmark superliga'] },
  { id: 'ekstraklasa', name: 'Ekstraklasa', category: 'league', icon: '🇵🇱', aliases: ['ekstraklasa', 'polish league'] },
  { id: 'czech-first-league', name: 'Czech First League', category: 'league', icon: '🇨🇿', aliases: ['czech first league', 'czech liga', 'fortune liga'] },
  { id: 'croatian-hnl', name: 'Croatian HNL', category: 'league', icon: '🇭🇷', aliases: ['croatian hnl', 'hnl', 'croatian league'] },
  { id: 'serbian-superliga', name: 'Serbian SuperLiga', category: 'league', icon: '🇷🇸', aliases: ['serbian superliga', 'superliga serbia'] },
  { id: 'romanian-superliga', name: 'Romanian SuperLiga', category: 'league', icon: '🇷🇴', aliases: ['romanian superliga', 'superliga romania'] },
  { id: 'j1-league', name: 'J1 League', category: 'league', icon: '🇯🇵', aliases: ['j1 league', 'j league', 'japan j1'] },
  { id: 'k-league', name: 'K League', category: 'league', icon: '🇰🇷', aliases: ['k league', 'k league 1', 'korean league'] },
  { id: 'chinese-super-league', name: 'Chinese Super League', category: 'league', icon: '🇨🇳', aliases: ['chinese super league', 'csl'] },
  { id: 'a-league', name: 'A-League', category: 'league', icon: '🇦🇺', aliases: ['a league', 'a-league', 'australian league'] },
  { id: 'indian-super-league', name: 'Indian Super League', category: 'league', icon: '🇮🇳', aliases: ['indian super league', 'isl'] },
  { id: 'euroleague', name: 'EuroLeague', category: 'league', icon: '🏀', aliases: ['euroleague', 'euroleague basketball'] },
  { id: 'wnba', name: 'WNBA', category: 'league', icon: '🏀', aliases: ['wnba'] },
  { id: 'ipl', name: 'IPL', category: 'league', icon: '🏏', aliases: ['ipl', 'indian premier league'] },
  { id: 'pakistan-super-league', name: 'Pakistan Super League', category: 'league', icon: '🏏', aliases: ['pakistan super league', 'psl'] },
  { id: 'big-bash', name: 'Big Bash League', category: 'league', icon: '🏏', aliases: ['big bash', 'big bash league', 'bbl'] },
  // Regional Leagues
  { id: 'lebanon-league', name: 'Lebanon League', category: 'regional', icon: '🇱🇧', aliases: ['lebanon league', 'lebanese league', 'el dawre el lebnene', 'dawri lebnene', 'الدوري اللبناني'] },
  { id: 'egypt-league', name: 'Egypt League', category: 'regional', icon: '🇪🇬', aliases: ['egypt league', 'egyptian league', 'el dawre el masre', 'الدوري المصري'] },
  { id: 'saudi-league', name: 'Saudi League', category: 'regional', icon: '🇸🇦', aliases: ['saudi league', 'saudi pro league', 'el dawre el sa3ude', 'الدوري السعودي'] },
  { id: 'uae-league', name: 'UAE League', category: 'regional', icon: '🇦🇪', aliases: ['uae league', 'emirates league', 'el dawre el imarate', 'الدوري الاماراتي'] },
  { id: 'qatar-league', name: 'Qatar League', category: 'regional', icon: '🇶🇦', aliases: ['qatar league', 'qatar stars league', 'el dawre el qatare', 'الدوري القطري'] },
  { id: 'morocco-league', name: 'Morocco League', category: 'regional', icon: '🇲🇦', aliases: ['morocco league', 'moroccan league', 'el dawre el maghribe', 'الدوري المغربي'] },
  { id: 'tunisia-league', name: 'Tunisia League', category: 'regional', icon: '🇹🇳', aliases: ['tunisia league', 'tunisian league', 'el dawre el tunse', 'الدوري التونسي'] },
  { id: 'jordan-league', name: 'Jordan League', category: 'regional', icon: '🇯🇴', aliases: ['jordan league', 'jordanian league', 'el dawre el urdone', 'الدوري الاردني'] },
  { id: 'kuwait-league', name: 'Kuwait League', category: 'regional', icon: '🇰🇼', aliases: ['kuwait league', 'kuwaiti league', 'el dawre el kuwaiti', 'الدوري الكويتي'] },
  { id: 'bahrain-league', name: 'Bahrain League', category: 'regional', icon: '🇧🇭', aliases: ['bahrain league', 'bahraini league', 'el dawre el bahraini', 'الدوري البحريني'] },
  { id: 'iraq-league', name: 'Iraq League', category: 'regional', icon: '🇮🇶', aliases: ['iraq league', 'iraqi league', 'el dawre el iraqi', 'الدوري العراقي'] },
  { id: 'oman-league', name: 'Oman League', category: 'regional', icon: '🇴🇲', aliases: ['oman league', 'omani league', 'el dawre el omani', 'الدوري العماني'] },
  { id: 'algeria-league', name: 'Algeria League', category: 'regional', icon: '🇩🇿', aliases: ['algeria league', 'algerian league', 'el dawre el jazairi', 'الدوري الجزائري'] },
  { id: 'syria-league', name: 'Syria League', category: 'regional', icon: '🇸🇾', aliases: ['syria league', 'syrian league', 'el dawre el souri', 'الدوري السوري'] },
  { id: 'libya-league', name: 'Libya League', category: 'regional', icon: '🇱🇾', aliases: ['libya league', 'libyan league', 'el dawre el libi', 'الدوري الليبي'] },
  { id: 'palestine-league', name: 'Palestine League', category: 'regional', icon: '🇵🇸', aliases: ['palestine league', 'palestinian league', 'el dawre el filastini', 'الدوري الفلسطيني'] },
  { id: 'south-african-premiership', name: 'South African Premiership', category: 'regional', icon: '🇿🇦', aliases: ['south african premiership', 'dstv premiership', 'psl south africa'] },
  // Special Categories
  { id: 'youth-sports', name: 'Youth Sports', category: 'special', icon: '👶', aliases: ['youth', 'youth sports', 'under 21', 'u21', 'shabab'] },
  { id: 'womens-sports', name: "Women's Sports", category: 'special', icon: '👩', aliases: ['women', 'womens sports', 'ladies', 'female sports', 'sayyidat'] },
  { id: 'asian-sports', name: 'Asian Sports', category: 'special', icon: '🌏', aliases: ['asian sports', 'asia', 'asian leagues'] },
  { id: 'esports', name: 'E-Sports', category: 'special', icon: '🎮', aliases: ['esports', 'e sports', 'electronic sports', 'gaming'] },
  { id: 'atp', name: 'ATP', category: 'special', icon: '🎾', aliases: ['atp', 'atp tour'] },
  { id: 'wta', name: 'WTA', category: 'special', icon: '🎾', aliases: ['wta', 'wta tour'] },
  { id: 'formula-1', name: 'Formula 1', category: 'special', icon: '🏁', aliases: ['formula 1', 'formula one', 'f1'] },
  { id: 'ufc', name: 'UFC', category: 'special', icon: '🥊', aliases: ['ufc', 'ultimate fighting'] },
  // Special Markets
  { id: 'saves-market', name: 'Saves Total Market', category: 'market', icon: '🥅', aliases: ['saves', 'saves market', 'goalkeeper saves', 'total saves'] },
  { id: 'corners-market', name: 'Corners Market', category: 'market', icon: '📐', aliases: ['corners', 'corners market', 'corner kicks', 'kornar'] },
  { id: 'cards-market', name: 'Cards Market', category: 'market', icon: '🟨', aliases: ['cards', 'cards market', 'yellow cards', 'red cards', 'kroot'] },
  { id: 'goals-market', name: 'Goals Market', category: 'market', icon: '⚽', aliases: ['goals', 'goals market', 'total goals', 'over under goals', 'ahdaf'] },
  { id: 'penalties-market', name: 'Penalties Market', category: 'market', icon: '🎯', aliases: ['penalties', 'penalty market', 'penalty kicks', 'penalte'] },
  { id: 'offsides-market', name: 'Offsides Market', category: 'market', icon: '📊', aliases: ['offsides', 'offside market', 'offsayd'] }
];

export const ALL_SPORTS_EVENT_ID = 'all-sports';

// Categories the shared catalog accepts for a new entry. 'universal' is reserved
// for all-sports, so a league captured off a bet has to land in one of these.
export const CUSTOM_EVENT_CATEGORIES = ['event', 'league', 'regional', 'special', 'market'];

// Helper to get combined sports events (built-in + custom)
export const getAllSportsEvents = (customEvents = []) => [...SPORTS_EVENTS, ...customEvents];

// Helper function to guess provider from game ID or name
export const getGameProvider = (gameId, gameName) => {
  const idLower = String(gameId || '').toLowerCase();
  const nameLower = String(gameName || '').toLowerCase();

  // Check for provider patterns in game ID
  if (idLower.includes('vs') && (idLower.includes('way') || idLower.includes('lion') || idLower.includes('sweet'))) return 'pragmatic';
  if (idLower.startsWith('evo') || idLower.includes('evolution')) return 'evo-evolution';
  if (idLower.includes('netent')) return 'evo-netent';
  if (idLower.includes('redtiger')) return 'evo-redtiger';
  if (idLower.includes('pgsoft') || idLower.startsWith('pg')) return 'pgsoft';
  if (idLower.includes('hacksaw')) return 'hacksaw';
  if (idLower.includes('popok')) return 'popok';
  if (idLower.includes('booming')) return 'booming';
  if (idLower.includes('playson')) return 'playson';
  if (idLower.includes('mascot')) return 'mascot';
  if (idLower.includes('worldmatch') || idLower.includes('wm')) return 'worldmatch';
  if (idLower.includes('fazi')) return 'fazi';
  if (idLower.includes('amusnet')) return 'amusnet';
  if (idLower.includes('pascal')) return 'pascal-gaming';
  if (idLower.includes('creed') || idLower.includes('roomz')) return 'creedroomz';
  if (idLower.includes('galaxsys') || idLower.includes('digitain')) return 'digitain-galaxsys';
  if (idLower.includes('pateplay')) return 'pateplay';

  // Check for patterns in game name
  if (nameLower.includes('pragmatic')) return 'pragmatic';
  if (nameLower.includes('evolution')) return 'evo-evolution';
  if (nameLower.includes('netent')) return 'evo-netent';
  if (nameLower.includes('red tiger')) return 'evo-redtiger';
  if (nameLower.includes('pg soft')) return 'pgsoft';
  if (nameLower.includes('hacksaw')) return 'hacksaw';
  if (nameLower.includes('booming')) return 'booming';
  if (nameLower.includes('playson')) return 'playson';

  return 'unknown';
};

export const getTypeIcon = (type) => {
  switch (type) {
    case USER_TYPES.ADMIN:
      return BuildingOfficeIcon;
    case USER_TYPES.COUNTRY_ADMIN:
      return GlobeAsiaAustraliaIcon;
    case USER_TYPES.CITY_ADMIN:
      return BuildingStorefrontIcon;
    case USER_TYPES.SUPER_AGENT:
      return StarIcon;
    case USER_TYPES.AGENT:
      return UserGroupIcon;
    case USER_TYPES.PLAYER:
    default:
      return UserIcon;
  }
};

export const getTypeColor = (type) => {
  switch (type) {
    case USER_TYPES.ADMIN:
      return 'text-purple-600 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/30';
    case USER_TYPES.COUNTRY_ADMIN:
      return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30';
    case USER_TYPES.CITY_ADMIN:
      return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30';
    case USER_TYPES.SUPER_AGENT:
      return 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/30';
    case USER_TYPES.AGENT:
      return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/30';
    case USER_TYPES.PLAYER:
    default:
      return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-800';
  }
};

const TYPE_LABELS = {
  [USER_TYPES.ADMIN]: 'Admin',
  [USER_TYPES.COUNTRY_ADMIN]: 'Country',
  [USER_TYPES.CITY_ADMIN]: 'City',
  [USER_TYPES.SUPER_AGENT]: 'Super Agent',
  [USER_TYPES.AGENT]: 'Agent',
  [USER_TYPES.PLAYER]: 'Player'
};

export const getTypeLabel = (type) => TYPE_LABELS[type] || 'Player';

// ---------------------------------------------------------------------------
// Matching a raw name off a bet back onto the catalog
// ---------------------------------------------------------------------------

/** Same slug rule the catalog Lambda applies, so a preview id matches what gets stored. */
export const toEventId = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const normalizeKey = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\u0600-\u06ff]+/g, ' ')
    .trim();

// Below this length an alias matches too much to be trusted as a substring
// ("can", "epl"), so short keys only ever win on a whole-string match.
const MIN_SUBSTRING_KEY_LENGTH = 4;

const eventKeys = (event) =>
  [event.name, event.id, ...(event.aliases || [])]
    .filter(Boolean)
    .map(normalizeKey)
    .filter(Boolean);

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const asWholeWords = (phrase, needle) =>
  new RegExp(`(^| )${escapeRegExp(phrase)}( |$)`).test(needle);

// Words that name a bucket of the catalog rather than one competition. Typing
// "league" has to offer every league, including NBA, which never uses the word.
const CATEGORY_QUERIES = {
  league: ['league', 'regional'],
  leagues: ['league', 'regional'],
  regional: ['regional'],
  event: ['event'],
  events: ['event'],
  tournament: ['event'],
  tournaments: ['event'],
  market: ['market'],
  markets: ['market'],
  special: ['special']
};

/**
 * Whether a catalog row belongs in the picker for this search.
 * A category word ("league", "market") matches the whole bucket. Anything else
 * matches the name, the id and the aliases.
 */
export const sportsEventMatchesQuery = (event, query) => {
  const term = normalizeKey(query);
  if (!term) return true;
  // "league" means the league buckets, not every cup whose alias happens to
  // contain the word. A real name ("eredivisie", "fa cup") still matches text.
  if (CATEGORY_QUERIES[term]) return CATEGORY_QUERIES[term].includes(event.category);
  return [event.name, event.id, event.category, ...(event.aliases || [])]
    .filter(Boolean)
    .some((value) => normalizeKey(value).includes(term));
};

/**
 * Resolve a provider's league/tournament string ("England Premier League") onto a
 * catalog entry. Returns null when nothing is close enough, which is the signal to
 * offer creating the league as a new custom event.
 *
 * @returns {{ event: Object, confidence: 'exact'|'partial' }|null}
 */
export const matchSportsEvent = (rawName, customEvents = []) => {
  const needle = normalizeKey(rawName);
  if (!needle) return null;

  const catalog = getAllSportsEvents(customEvents);

  for (const event of catalog) {
    if (eventKeys(event).includes(needle)) {
      return { event, confidence: 'exact' };
    }
  }

  // Longest key wins so "Premier League" beats the bare "england" alias when a
  // tournament string happens to contain both. A reordered name ("Premier League
  // Russia") still belongs to the league whose full name is present.
  let best = null;
  for (const event of catalog) {
    for (const key of eventKeys(event)) {
      if (key.length < MIN_SUBSTRING_KEY_LENGTH) continue;
      const words = key.split(' ').filter(Boolean);
      const contiguous = asWholeWords(key, needle);
      const scattered = words.length >= 2 && words.every((part) => asWholeWords(part, needle));
      if (!contiguous && !scattered) continue;
      const wordCount = words.length;
      if (
        !best ||
        key.length > best.length ||
        (key.length === best.length && wordCount > best.words)
      ) {
        best = { event, length: key.length, words: wordCount };
      }
    }
  }

  return best ? { event: best.event, confidence: 'partial' } : null;
};

/**
 * Map a display provider label ("Pragmatic Play") back onto a GAME_PROVIDERS id,
 * falling back to sniffing the game code the way the Limits page does.
 */
export const resolveProviderId = (providerLabel, gameCode, gameName) => {
  const label = normalizeKey(providerLabel);

  if (label) {
    const direct = GAME_PROVIDERS.find(
      (provider) =>
        provider.id !== 'all' &&
        (normalizeKey(provider.name) === label || normalizeKey(provider.id) === label)
    );
    if (direct) return direct.id;
  }

  const sniffed = getGameProvider(gameCode, gameName || providerLabel);
  return sniffed === 'unknown' ? null : sniffed;
};

export const getProviderName = (providerId) =>
  GAME_PROVIDERS.find((provider) => provider.id === providerId)?.name || providerId;

// ---------------------------------------------------------------------------
// Limit scopes
// ---------------------------------------------------------------------------

/**
 * A scope describes *what* a limit covers, independent of who it is attached to
 * and which currency it is priced in.
 *
 * @typedef {Object} LimitScope
 * @property {'sports'|'casino'} kind
 * @property {'sports'|'sports-event'|'universal'|'provider'|'game'} limitType
 * @property {string} key            stable id for radio selection
 * @property {string} label          headline shown on the chip
 * @property {string} [description]  secondary line on the chip
 * @property {string} [sportsEventId]
 * @property {string} [sportsEventName]
 * @property {string} [sportsEventCategory]
 * @property {string} [gameId]
 * @property {string} [providerId]
 * @property {Object} [pendingCatalogEvent] league that must be added to the shared catalog first
 */

const GENERAL_GAME_IDS = ['blackjack', 'roulette'];

/**
 * Table games that come in dozens of branded variants. A limit on one of these
 * ids covers the whole family, which is almost always what an admin means: they
 * want to cap roulette, not "XXXtreme Lightning Roulette" specifically.
 *
 * `id` has to match the general option ids the Limits page writes, so a limit
 * set from a bet and one set by hand land on the same record.
 */
export const GENERAL_GAME_FAMILIES = [
  { id: 'blackjack', name: 'Blackjack', pattern: /blackjack|black jack/i },
  { id: 'roulette', name: 'Roulette', pattern: /roulette|ruleta/i },
  { id: 'baccarat', name: 'Baccarat', pattern: /baccarat|punto banco/i }
];

/** Which family a specific game belongs to, read off its code and its name. */
export const getGeneralGameFamily = (gameCode, gameName) => {
  const haystack = `${gameCode || ''} ${gameName || ''}`;
  return GENERAL_GAME_FAMILIES.find((family) => family.pattern.test(haystack)) || null;
};

/** The (limitType, identifier) pair the backend dedups on. */
export const getScopeIdentity = (scope) => {
  if (!scope) return null;

  switch (scope.limitType) {
    case 'sports':
      return { limitType: 'sports', identifier: 'sports' };
    case 'sports-event':
      return { limitType: 'sports-event', identifier: `sports:${scope.sportsEventId}` };
    case 'universal':
      return { limitType: 'universal', identifier: 'all' };
    case 'provider':
      return { limitType: 'provider', identifier: scope.providerId };
    case 'game':
    default:
      return {
        limitType: GENERAL_GAME_IDS.includes(scope.gameId) ? 'game-general' : 'game',
        identifier: scope.gameId
      };
  }
};

/**
 * Build the limit body the /core/limits endpoints expect. One record per currency:
 * the table stores a single currency per limit, so covering several currencies means
 * saving several records.
 */
export const buildLimitPayload = (scope, currency, maxBet, minBet = 0) => {
  const identity = getScopeIdentity(scope);
  const base = { ...identity, currency, maxBet, minBet };

  switch (scope.limitType) {
    case 'sports':
      return { ...base, gameId: 'sports' };
    case 'sports-event':
      return {
        ...base,
        sportsEventId: scope.sportsEventId,
        sportsEventName: scope.sportsEventName || scope.sportsEventId,
        sportsEventCategory: scope.sportsEventCategory || 'other',
        gameId: 'sports'
      };
    case 'universal':
      return { ...base, gameId: 'all' };
    case 'provider':
      return { ...base, providerId: scope.providerId };
    case 'game':
    default:
      return { ...base, gameId: scope.gameId };
  }
};

// A maximum of exactly 0 closes the scope rather than capping it, so it must
// read as a block everywhere instead of as a "0" the eye slides past.
export const isBlockingLimit = (limit) => Number(limit?.maxBet) === 0;

/** True when a stored limit covers exactly this scope (ignoring currency). */
export const limitMatchesScope = (limit, scope) => {
  const identity = getScopeIdentity(scope);
  if (!limit || !identity) return false;

  // 'game-general' is sent by the UI but the limits Lambda writes those back as
  // plain 'game', so both spellings have to count as the same scope.
  const sameType =
    limit.limitType === identity.limitType ||
    (identity.limitType === 'game-general' && limit.limitType === 'game');

  return sameType && limit.identifier === identity.identifier;
};

/** Turn a sports catalog entry into a scope. */
export const sportsEventToScope = (event) => {
  if (!event || event.id === ALL_SPORTS_EVENT_ID) {
    return {
      kind: 'sports',
      limitType: 'sports',
      key: 'sports:all',
      label: 'All sports',
      description: 'Every sports bet'
    };
  }

  return {
    kind: 'sports',
    limitType: 'sports-event',
    key: `sports:${event.id}`,
    label: event.name,
    description: event.category,
    icon: event.icon,
    sportsEventId: event.id,
    sportsEventName: event.name,
    sportsEventCategory: event.category
  };
};

/**
 * Work out what an admin can limit from a single bet, most specific first.
 * For sports that is the league it was placed in (resolved onto the shared
 * catalog, or queued for creation) and then all sports; for casino it is the
 * game, its provider, and then every casino game.
 *
 * @param {Object} bet
 * @param {boolean} bet.isSports
 * @param {string} [bet.leagueName]     tournament/competition off the bet
 * @param {string} [bet.gameCode]       raw casino game code
 * @param {string} [bet.gameName]       display name for the casino game
 * @param {string} [bet.providerLabel]  display provider off the bet
 * @param {Array}  customEvents         shared catalog additions
 * @returns {{ options: LimitScope[], unmatchedLeague: string|null, matchConfidence: string|null }}
 */
export const buildBetScopeOptions = (bet, customEvents = []) => {
  if (!bet) return { options: [], unmatchedLeague: null, matchConfidence: null };

  const allSportsScope = sportsEventToScope(null);

  if (bet.isSports) {
    const options = [];
    let unmatchedLeague = null;
    let matchConfidence = null;

    const league = String(bet.leagueName || '').trim();
    if (league) {
      const match = matchSportsEvent(league, customEvents);
      if (match && match.event.id !== ALL_SPORTS_EVENT_ID) {
        matchConfidence = match.confidence;
        options.push({
          ...sportsEventToScope(match.event),
          description: `${match.event.category} • from "${league}"`
        });
      } else if (!match) {
        unmatchedLeague = league;
        options.push({
          kind: 'sports',
          limitType: 'sports-event',
          key: `sports:new:${toEventId(league)}`,
          label: league,
          description: 'New league — will be added to the shared catalog',
          sportsEventId: toEventId(league),
          sportsEventName: league,
          sportsEventCategory: 'league',
          pendingCatalogEvent: {
            name: league,
            category: 'league',
            icon: '🏆',
            aliases: [league]
          }
        });
      }
    }

    options.push(allSportsScope);
    return { options, unmatchedLeague, matchConfidence };
  }

  const options = [];

  if (bet.gameCode) {
    options.push({
      kind: 'casino',
      limitType: 'game',
      key: `game:${bet.gameCode}`,
      label: bet.gameName || bet.gameCode,
      description: 'This game only',
      gameId: bet.gameCode
    });
  }

  // Every branded variant of the same table game, offered next to the single
  // variant so the choice between "this roulette" and "all roulette" is made
  // deliberately rather than by whichever one happened to be listed.
  // `generalGameId` lets a caller name the family outright, for when it is
  // already known — a refusal, for instance, knows which family capped it.
  const familyId =
    bet.generalGameId || getGeneralGameFamily(bet.gameCode, bet.gameName)?.id || null;
  const family = GENERAL_GAME_FAMILIES.find((entry) => entry.id === familyId);

  if (family && family.id !== bet.gameCode) {
    options.push({
      kind: 'casino',
      limitType: 'game',
      key: `game:${family.id}`,
      label: `${family.name} (all variants)`,
      description: 'Every version of this game, from any provider',
      gameId: family.id
    });
  }

  const providerId = resolveProviderId(bet.providerLabel, bet.gameCode, bet.gameName);
  if (providerId) {
    options.push({
      kind: 'casino',
      limitType: 'provider',
      key: `provider:${providerId}`,
      label: getProviderName(providerId),
      description: 'Every game from this provider',
      providerId
    });
  }

  options.push({
    kind: 'casino',
    limitType: 'universal',
    key: 'universal',
    label: 'All casino games',
    description: 'Every game, every provider'
  });

  return { options, unmatchedLeague: null, matchConfidence: null };
};

// ---------------------------------------------------------------------------
// Hierarchy
// ---------------------------------------------------------------------------

const baseUsername = (username) => String(username || '').split('-')[0].toLowerCase();

/**
 * Root-to-user path through the tree. A limit is targeted by attaching it to one of
 * these usernames, so this is the ladder an admin walks up to widen a limit from a
 * single player to their agent, city or country.
 */
export const findUserChain = (tree, username) => {
  if (!tree || !username) return [];

  const target = baseUsername(username);

  const walk = (node, trail) => {
    if (!node) return null;
    const path = [...trail, node];
    if (baseUsername(node.username) === target) return path;

    for (const child of node.children || []) {
      const found = walk(child, path);
      if (found) return found;
    }
    return null;
  };

  return walk(tree, []) || [];
};

/** Currencies a node can actually hold, used to offer sensible limit currencies. */
export const getNodeCurrencies = (node) => {
  if (!node) return [];
  const fromBalances = Object.keys(node.balances || {});
  const fromAttributes = node.attributes?.currencies || [];
  return Array.from(new Set([...fromBalances, ...fromAttributes])).filter(Boolean);
};
